/* The console's view of the router's domain.
 *
 * These mirror the Go models field-for-field, by their JSON tags, so that
 * turning on a real data source is a type-checked change rather than guesswork:
 *
 *   Estate, Realm, ProviderPolicy, PoolScope,
 *   RateLimits, BillingConfig, Deployment,
 *   RouterTokenRecord, LLMProviderKey      api/internal/types/types.go
 *   UsageRow, TokenUsage                   api/internal/types/usage.go
 *   ClientKey                              api/internal/control/exchange.go
 *   PoolKey, QuotaWindow                   api/db/llmoauth/types.go
 *
 * Timestamps arrive as RFC3339 strings over HTTP and as Firestore Timestamps
 * over the SDK; both are normalised to an ISO string at the source boundary, so
 * nothing above the source layer ever sees two shapes. */

export type EstateID = string;
export type TenantID = string;

/** A tenant is only unique WITHIN an estate — two estates may use the same
 *  tenant id, and the backend never says otherwise (the ledger's dedup identity
 *  is (estate, tenant, event)). So every tenant-keyed structure in the console
 *  keys on this, never on the bare tenant id. */
export type TenantKey = `${EstateID}/${TenantID}`;

export function tenantKey(estateId: EstateID, tenantId: TenantID): TenantKey {
  return `${estateId}/${tenantId}`;
}

export function splitTenantKey(key: TenantKey): {
  estateId: EstateID;
  tenantId: TenantID;
} {
  const at = key.indexOf('/');
  return { estateId: key.slice(0, at), tenantId: key.slice(at + 1) };
}

// ---------------------------------------------------------------- estate

export interface Realm {
  name: string;
  issuer: string;
  audience: string;
  jwksUrl: string;
  native: boolean;
  /** Set only when the realm's traffic metric reads zero — never on a calendar. */
  retireAfter?: string;
  tenantClaim: string;
}

export interface ProviderPolicy {
  allowed?: string[];
  /** The per-estate kill switch for a model that turns out mispriced. */
  modelDeny?: string[];
  allowByoKeys: boolean;
}

export interface PoolScope {
  enabled: boolean;
  reserved?: string[];
  useShared: boolean;
}

export interface RateLimits {
  attributedPerMinute: number;
  billablePerMinute: number;
  completeTimeoutSeconds?: number;
}

export type EnvelopeLane = 'event_key' | 'composed';

export interface BillingConfig {
  eventLane: EnvelopeLane;
  eventType: string;
  exemptTenants?: string[];
}

/** What an estate stores of its customers' prompts and responses.
 *
 *  Mirrors `types.CaptureConfig`. Absent reads as off, which is the only safe
 *  default: a config that failed to load must not start storing prompts. */
export interface CaptureConfig {
  mode?: '' | 'errors' | 'sampled' | 'full';
  sampleRate?: number;
  retentionHours?: number;
}

export interface Estate {
  id: EstateID;
  displayName: string;
  enabled: boolean;
  realms: Realm[];
  providers: ProviderPolicy;
  pool: PoolScope;
  limits: RateLimits;
  billing: BillingConfig;
  capture?: CaptureConfig;
  /** The estate's long-term-memory policy (DR-73). Absent reads as off. */
  memory?: MemoryConfig;
  createdAt: string;
  updatedAt: string;
}

/** MemoryConfig mirrors `types.MemoryConfig`: mode "on" enables /v1/memory/*
 *  for the estate; the cap is per tenant, 0 = the router's default (10000).
 *  There is no deployment-level override — a per-deployment cap would not
 *  be a tenant-wide quota. Changed with `haus memory set`. */
export interface MemoryConfig {
  mode?: '' | 'on' | 'off';
  maxMemoriesPerTenant?: number;
}

// ------------------------------------------------------------ deployment

export interface Deployment {
  /** "<estate>/<tenant>@<name>". The Firestore doc id maps "/" to "~". */
  id: string;
  estateId: EstateID;
  tenantId: TenantID;
  label: string;
  enabled: boolean;
  providerAllow?: string[];
  /** A deployment may only TIGHTEN the estate's limits. */
  limits?: RateLimits | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ----------------------------------------------------------------- token

export type BillingMode = 'billable' | 'attributed';

export interface RouterTokenRecord {
  /** Note: `tokenId`, not `id`. */
  tokenId: string;
  estateId: EstateID;
  tenantId: TenantID;
  deploymentId: string;
  /** SHA-256 of the minted bytes. Never the token. */
  tokenSha256?: string;
  issuedAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  billingMode?: string;
}

/** An EMPTY billingMode reads as BILLABLE, not attributed (DR-13): every legacy
 *  record omits it and every one of them is billable. The attributed default
 *  applies at MINT, not at read — so a mint form defaults the other way. */
export function effectiveBillingMode(rec: {
  billingMode?: string;
}): BillingMode {
  return rec.billingMode === 'attributed' ? 'attributed' : 'billable';
}

export function isRevoked(rec: { revokedAt?: string | null }): boolean {
  return Boolean(rec.revokedAt);
}

// ------------------------------------------------------------ client key

export interface ClientKey {
  /** "rk-" + the first 16 hex of the RFC 7638 thumbprint. Derived, so one key
   *  can never be registered under two names. */
  id: string;
  /** The PUBLIC half only. Registration refuses private material outright. */
  publicJwk: string;
  estateId: EstateID;
  tenantId: TenantID;
  deploymentId: string;
  label: string;
  enabled: boolean;
  expiresAt?: string;
  revokedAt?: string;
  lastUsedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ------------------------------------------------------ tenant BYO key

/** The sealed tenant key. `keyRef` is json:"-" in Go and is absent here by
 *  construction: this console can never read key material, and there is no
 *  "reveal" affordance to build. */
export interface TenantProviderKey {
  id: string;
  estateId: EstateID;
  tenantId: TenantID;
  provider: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// ------------------------------------------------------------- pool key

export type PoolKeyKind = '' | 'oauth' | 'api_key';

export interface QuotaWindow {
  kind: string;
  label: string;
  usedPercent: number;
  resetAt?: string;
  windowMinutes: number;
  status: string;
}

/** One credential in the shared pool. `payload` and `keyRef` are json:"-" in Go
 *  and absent here — see spec/console.md: a Firestore rule grants a whole
 *  document, which is exactly why this collection is not browser-watchable. */
export interface PoolKey {
  id: string;
  provider: string;
  label: string;
  kind: PoolKeyKind;
  /** "" means shared across every estate; otherwise an estate id. */
  scope: string;
  enabled: boolean;
  expiresAt?: string;
  lastRefresh?: string;
  lastUsedAt?: string;
  rotationEpoch: number;
  disabledUntil?: string;
  /** "quota" | "refresh_failed" | "manual" */
  disabledReason?: string;
  quotaResetAt?: string;
  quotaUsedPercent: number;
  quotaRemaining: number;
  quotaWindows?: QuotaWindow[];
  planType?: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  failureCount: number;
  lastFailureAt?: string;
  /** Already scrubbed through RedactSecrets server-side. */
  lastError?: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export type PoolKeyState = 'ACTIVE' | 'RESTED' | 'QUOTA PAUSED' | 'DISABLED' | 'EXHAUSTED';

/** Selection admits only enabled ∧ not rested ∧ not quota-paused. The three are
 *  distinct states and an operator acts differently on each, so they are
 *  derived here once rather than at each call site. */
export function poolKeyState(key: PoolKey, now = Date.now()): PoolKeyState {
  // Resting is automatic and the operator switch is manual; a key can be both,
  // and the automatic state is the one worth reporting because it is the one
  // that will change on its own. `enabled` has its own column.
  const until = key.disabledUntil ? Date.parse(key.disabledUntil) : 0;
  if (until > now) {
    return key.disabledReason === 'quota' ? 'QUOTA PAUSED' : 'RESTED';
  }
  // Off by the pool's hand, not the operator's: the provider said the
  // account's funding is spent, and an API key has no window that turns. The
  // operator's next move (pay, test, enable) differs from a manual disable,
  // so it is its own state.
  if (!key.enabled) return key.disabledReason === 'exhausted' ? 'EXHAUSTED' : 'DISABLED';
  return 'ACTIVE';
}

/** Selection admits enabled ∧ not resting. Both conditions, independently. */
export function poolKeySelectable(key: PoolKey, now = Date.now()): boolean {
  const until = key.disabledUntil ? Date.parse(key.disabledUntil) : 0;
  return key.enabled && !(until > now);
}

/** What a pool key's `expiresAt` names. For anthropic and openai it is the
 *  access token the sweep (DR-42) keeps renewing off a rotating refresh token.
 *  For meta it is the model key minted from a durable identity token that does
 *  not rotate — re-minted on demand, so that key running out is routine, not a
 *  lapse; the identity token itself has no readable expiry. An API key has no
 *  short-lived half at all. Mirrors `providerRotatesRefreshToken` in
 *  api/db/llmoauth, where meta is likewise the deliberate exception (DR-57). */
export type PoolKeyExpiry = 'access_token' | 'model_key' | 'none';

export function poolKeyExpiry(key: Pick<PoolKey, 'provider' | 'kind'>): PoolKeyExpiry {
  if (key.kind === 'api_key') return 'none';
  switch (key.provider) {
    case 'anthropic':
    case 'openai':
    case 'kimi-code':
      return 'access_token';
    case 'meta':
      return 'model_key';
    default:
      return 'none';
  }
}

/** An enabled rotating key whose access token has ALREADY expired.
 *
 *  "Expiring soon" is not a signal in this pool: an Anthropic access token
 *  lives eight hours, so every key is always expiring soon, and the sweep is
 *  what makes that fine — it renews inside a window wider than its own cadence,
 *  so a healthy key never lapses. A lapse therefore means the renewal is not
 *  happening, which is the one expiry an operator should act on. A disabled key
 *  is skipped by the sweep and lapses by design; a rested one is swept — a rest
 *  is about quota, not credentials — and counts. */
export function poolKeyLapsed(key: PoolKey, now = Date.now()): boolean {
  if (!key.enabled || poolKeyExpiry(key) !== 'access_token') return false;
  const at = key.expiresAt ? Date.parse(key.expiresAt) : Number.NaN;
  return !Number.isNaN(at) && at <= now;
}

// ------------------------------------------------------------ usage row

export interface TokenUsage {
  modelId: string;
  /** cachedTokens and cacheCreationTokens are SUBSETS of inputTokens, not
   *  disjoint classes. Summing the three double-counts. */
  inputTokens: number;
  cachedTokens: number;
  cacheCreationTokens: number;
  outputTokens: number;
  finishReason: string;
  retryReason: string;
}

export type UsageStatus =
  | 'ok'
  | 'provider_error'
  | 'canceled'
  | 'invalid_request'
  | 'provider_unavailable'
  | 'rate_limited'
  | 'internal';

export type AuthLane = 'token' | 'google';

export interface UsageRow {
  /** The ledger document id: SHA-256/128 over the length-prefixed dedup tuple.
   *  It is json:"-" in Go, so an HTTP source must reattach it from the read. */
  id: string;

  estateId: EstateID;
  tenantId: TenantID;
  eventId: string;
  lane: EnvelopeLane | '';

  /** Router-generated, uninfluenceable by any caller. */
  requestId: string;

  deploymentId: string;
  subject: string;
  authLane: AuthLane | '';
  billingMode: string;
  billable: boolean;

  workId: string;
  agentName: string;
  callKey: string;
  seq: number;
  workstreamId: string;
  seatId: string;
  subjectRef: string;
  taskType: string;

  provider: string;
  /** What billing keys on: the pinned id the caller asked for. */
  requestModel: string;
  /** The provider's dated echo. */
  servedModel: string;
  status: UsageStatus | string;
  latencyMs: number;

  poolKeyId?: string;

  primary: TokenUsage;
  /** A refusal fallback's own consumption, billed at "<eventId>:refused". */
  refused?: TokenUsage | null;

  /** Routing. Absent on an unrouted request. requestModel above is the
   *  concrete model that served; the alias the caller sent ("auto",
   *  "auto:fast", or the pinned ceiling under forced routing) rides here with
   *  the tier it landed on and the decision trail. */
  routedFrom?: string;
  routeTier?: string;
  routeReason?: string;
  /** The classifier a routed call spent, billed at "<eventId>:router" — its
   *  own provider and credential, which may never serve a completion. */
  router?: RouterUsage | null;

  /** Set on a POST /v1/search row and nothing else: the unit beside the
   *  grounded call's tokens (DR-68). */
  search?: SearchUsage | null;
  /** Set on a /v1/memory/* row and nothing else: zero tokens, the unit
   *  beside them, never billed (DR-73). */
  memory?: MemoryUsage | null;
  /** Set on a POST /v1/compact row and nothing else: the fold count is the
   *  unit beside the summariser's own tokens; the two token figures are
   *  telemetry about the history. A foldless pass (folds 0) is never
   *  billed (DR-76, DR-77). */
  compact?: CompactUsage | null;

  occurredAt: string;
  expireAt: string;
  /** Names the row this one collided with. The first write stands; this exists
   *  so the second call's real spend is not invisible. An incident, not a dedup. */
  conflictOf?: string;
}

export interface SearchUsage {
  provider: string;
  queries: number;
  results: number;
}

export interface MemoryUsage {
  op: 'remember' | 'recall' | 'forget' | 'list' | string;
  items: number;
  hits: number;
}

export interface CompactUsage {
  op: 'compact' | string;
  folds: number;
  tokensBefore: number;
  tokensAfter: number;
}

/** rowKind says what a usage row records: a completion, a grounded search,
 *  a memory operation or a context compaction. Counts and tiles filter on it. */
export function rowKind(row: Pick<UsageRow, 'search' | 'memory' | 'compact'>): 'completion' | 'search' | 'memory' | 'compact' {
  if (row.memory?.op) return 'memory';
  if (row.compact?.op) return 'compact';
  if (row.search?.provider) return 'search';
  return 'completion';
}

// ---------------------------------------------------------------- memory

export type MemoryStatus = 'active' | 'superseded' | 'deleted';

/** A memory as the admin API projects it (`adminapi.MemoryView`). Text,
 *  tags, scope ids and source refs are agent-written tenant content: render
 *  them as text nodes only, never as markup, never in a URL. */
export interface Memory {
  id: string;
  estateId: EstateID;
  tenantId: TenantID;
  scope: string;
  kind?: string;
  /** The one-line headline the writer gave it (DR-87). Absent on a memory
   *  written before titles were required. */
  title?: string;
  text: string;
  /** Optional topic paths ("engineering > deploy"), matched exactly (DR-87). */
  categories?: string[];
  tags?: string[];
  status: MemoryStatus | string;
  version: number;
  supersedes?: string[];
  supersededBy?: string;
  source: MemorySource;
  observedAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  /** False is a degraded success: stored, but the router's mirror into the
   *  index was not acknowledged; `haus memory reindex` heals it. Recall is
   *  index-only (DR-85), so this is not a recallability flag either way. */
  indexed: boolean;
}

export interface MemorySource {
  actor: 'user' | 'agent' | 'compaction' | 'operator' | string;
  ref?: string;
  subject?: string;
  deploymentId?: string;
}

export interface MemoryStats {
  active: number;
  superseded: number;
  deleted: number;
}

export interface MemoryListQuery {
  estateId: EstateID;
  tenantId: TenantID;
  scope?: string;
  kind?: string;
  status?: MemoryStatus | '';
  tag?: string;
  text?: string;
  limit: number;
  cursor?: string;
}

export interface MemoryPage {
  items: Memory[];
  nextCursor?: string;
}

/** One tenant's memory counts, from the estate-wide overview (DR-86). Both
 *  halves of the tuple: two estates may use the same tenant id. */
export interface MemoryTenant extends MemoryStats {
  estateId: string;
  tenantId: string;
}

export interface MemoryTenantsQuery {
  estateId: string;
  limit: number;
  cursor?: string;
}

export interface MemoryTenantPage {
  items: MemoryTenant[];
  nextCursor?: string;
}

/** The index's relevance cutoff for a recall (DR-85); lowest is the default. */
export type MemoryRelevance = 'lowest' | 'low' | 'medium' | 'high';

export interface MemoryRecallHit {
  rank: number;
  /** The index's own score: a relevance score, or a rank fallback. */
  score: number;
  channels: string[];
  /** Rank in the index's answer before hydration dropped anything. */
  indexRank?: number;
  memory: Memory;
}

export interface MemoryRecallResult {
  query: string;
  scopes: string[];
  relevance: MemoryRelevance;
  hits: MemoryRecallHit[];
  indexHits: number;
  indexMs: number;
}

/** RouterUsage is the classifier call a routed request spent, as its own
 *  entry with its own attribution. */
export interface RouterUsage {
  provider: string;
  requestModel: string;
  servedModel?: string;
  poolKeyId?: string;
  latencyMs: number;
  usage: TokenUsage;
  /** The verdict, kept for calibration; never the crux. */
  forecast?: RouterForecast | null;
}

export interface RouterForecast {
  card: string;
  rule?: string;
  boundary: string;
  pSolve: number;
  task?: string;
}

/** The entry suffixes are derived in exactly one place in Go, and in exactly
 *  one place here, for the same reason: two implementations of one suffix is how
 *  two systems stop agreeing about what a refusal — or a classifier — costs. */
export function entryId(eventId: string, kind: 'primary' | 'refused' | 'router'): string {
  return kind === 'primary' ? eventId : `${eventId}:${kind}`;
}

/** Total tokens a row consumed, counting the refusal fallback and the
 *  classifier. Input already contains the cached and cache-creation subsets,
 *  so they are NOT added. */
export function rowTokens(row: UsageRow): number {
  const of = (u: TokenUsage | null | undefined) =>
    u ? u.inputTokens + u.outputTokens : 0;
  return of(row.primary) + of(row.refused) + of(row.router?.usage);
}

// ---------------------------------------------------------- analytics

/** One point of a chart series. Charts come from BigQuery through the control
 *  plane, never from a Firestore watch. */
export interface SeriesPoint {
  /** The bucket label, already formatted for the axis by the query. */
  t: string;
  [metric: string]: string | number;
}

export type RangeKey = '24 hours' | '7 days' | '30 days';

/** The runtime twin of RangeKey, for validating a value that arrives from
 *  outside the type system — a persisted preference, a query string. */
export const RANGE_KEYS: readonly RangeKey[] = ['24 hours', '7 days', '30 days'];

export function isRangeKey(v: unknown): v is RangeKey {
  return typeof v === 'string' && (RANGE_KEYS as readonly string[]).includes(v);
}

export interface ProviderHealth {
  provider: string;
  shareOfCalls: number;
  p50Ms: number;
  p95Ms: number;
  errorRate: number;
}

export interface RealmShare {
  realm: string;
  share: number;
  native: boolean;
}

export interface ReconciliationDay {
  estateId: EstateID;
  verdict: string;
  detail: string;
  ok: boolean;
}

export interface TenantUsageTotal {
  estateId: EstateID;
  tenantId: TenantID;
  displayName: string;
  billingMode: BillingMode;
  /** Provider LIST cost. NEVER what the estate charged — the router does not
   *  price (DR-6), and this field exists only for the margin view. */
  listCostUsd: number;
  completions: number;
}

export interface OverviewTotals {
  completions: number;
  listCostUsd: number;
  billableShare: number;
  p95LatencyMs: number;
  billingConflicts: number;
}

export interface ModelShare {
  model: string;
  tokens: number;
  share: number;
}

// --------------------------------------------------- tenant (view model)

/** A tenant summary as the console shows it.
 *
 * PREVIEW-ONLY, and deliberately typed apart from the persisted models above:
 * there is no tenant collection and no tenant configuration in the router. A
 * tenant is a string carried on tokens, deployments, provider keys and usage
 * rows. When this is wired for real it will be DERIVED from those, and this
 * shape is the contract that derivation has to meet. */
export interface TenantSummary {
  key: TenantKey;
  estateId: EstateID;
  tenantId: TenantID;
  displayName: string;
  deployments: number;
  /** Sealed BYO keys. Today at most one per (estate, tenant, provider). */
  sealedKeys: number;
  billingMode: BillingMode;
  status: string;
  lane: EnvelopeLane | 'none';
  /** Provider LIST cost over the current range, or null when unknown. */
  listCostUsd: number | null;
}
